import React, { useState, useMemo } from 'react';
import { Activity, RefreshCw, BarChart3, Plus, Moon, Sun } from 'lucide-react';
import { DashboardProvider, useDashboard } from './context/DashboardContext';
import MetricsOverview from './components/MetricsOverview';
import DashboardTabs from './components/DashboardTabs';
import Settings from './components/Settings';
import Logo from './components/Logo';
import ErrorBanner from './components/ErrorBanner';
import ApiKeyPrompt from './components/ApiKeyPrompt';
import './App.css';

// Memoized AppContent component for better performance
const AppContent = React.memo(() => {
  const { 
    loading, 
    error, 
    lastUpdated, 
    refreshData, 
    handleEnvironmentChange, 
    isRefreshing, 
    currentEnvironment,
    showApiKeyPrompt,
    handleApiKeySubmit
  } = useDashboard();
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [dismissedError, setDismissedError] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  console.log('AppContent render:', { loading, error, lastUpdated, isRefreshing, currentEnvironment, showApiKeyPrompt });

  // Toggle theme
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.classList.toggle('dark-mode');
  };

  // Memoize navigation items to prevent unnecessary re-renders
  const navigationItems = useMemo(() => [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: BarChart3,
      active: currentPage === 'dashboard'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Plus,
      active: currentPage === 'settings'
    }
  ], [currentPage]);

  // Handle error dismissal
  const handleErrorDismiss = () => {
    setDismissedError(error);
  };

  // Show error banner if there's an error and it hasn't been dismissed
  const showErrorBanner = error && error !== dismissedError;

  // Show API key prompt if needed
  if (showApiKeyPrompt) {
    return <ApiKeyPrompt onApiKeySubmit={handleApiKeySubmit} />;
  }

  if (loading) {
    console.log(`${currentEnvironment}: Showing loading state`);
    return (
      <div className="loading">
        <Activity className="animate-spin" size={24} />
        <span style={{ marginLeft: '0.5rem' }}>{currentEnvironment}: Loading data...</span>
      </div>
    );
  }

  return (
    <div className="App">
      {showErrorBanner && (
        <ErrorBanner 
          error={error} 
          onDismiss={handleErrorDismiss}
        />
      )}
      
      <header className="app-header">
        <div className="container">
          <div className="header-content">
            <div className="header-left">
              <div className="header-brand">
                <Logo size={48} className="header-logo" />
                <div className="header-text">
                  <h1>FlightPath</h1>
                  <p>Navigate your projects with precision</p>
                </div>
              </div>
            </div>
            <div className="header-right">
              <div className="header-actions">
                <button 
                  onClick={toggleTheme} 
                  className="theme-toggle-button"
                  title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
                  {isDarkMode ? 'Light' : 'Dark'}
                </button>
                <nav className="main-nav">
                  {navigationItems.map(({ id, label, icon: Icon, active }) => (
                    <button 
                      key={id}
                      className={`nav-btn ${active ? 'active' : 'inactive'}`}
                      onClick={() => setCurrentPage(id)}
                    >
                      <Icon size={16} />
                      {label}
                    </button>
                  ))}
                </nav>
                <button 
                  onClick={refreshData} 
                  className="refresh-button"
                  disabled={isRefreshing}
                  title={isRefreshing ? 'Refreshing...' : 'Refresh data'}
                >
                  <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
                  {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </button>
                <div className="last-updated">
                  Last updated: {lastUpdated ? lastUpdated.toLocaleString() : 'Never'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container">
        {currentPage === 'dashboard' && (
          <>
            <MetricsOverview />
            <DashboardTabs />
          </>
        )}
        {currentPage === 'settings' && <Settings />}
      </main>
    </div>
  );
});

AppContent.displayName = 'AppContent';

function App() {
  return (
    <DashboardProvider>
      <AppContent />
    </DashboardProvider>
  );
}

export default App;
